/**
 * Which localStorage keys must be purged when the signed-in user changes.
 *
 * WHY THIS IS A PATTERN AND NOT A LIST
 * ------------------------------------
 * This used to be a hardcoded array of three names:
 *
 *     ['qyngent_active_project', 'qyngent:lastProject', 'qyngent:lastApplication']
 *
 * A brand-new account then inherited the PREVIOUS user's selected project
 * (reported 2026-09-06). Five project keys were sitting in the browser and NONE
 * of them matched that list:
 *
 *     active_project
 *     qyngent_active_project:<workspaceId>   <- suffixed, so even the near-match missed
 *     qyngent.active_project_id
 *     activeProjectId
 *     qyngent.activeProjectId
 *
 * Five names for one concept is its own problem, but the lesson is that a
 * hardcoded list silently stops matching the moment anyone adds a key — and the
 * failure is invisible, because nothing errors. So: match the app's namespaces,
 * and keep an explicit, short PRESERVE list for the things that must survive.
 *
 * Purging is deliberately biased towards over-clearing. Re-fetching state costs
 * a request; leaking one account's selection into another is a correctness and
 * privacy problem.
 */

/** Anything that is per-ACCOUNT state belonging to this app. */
const APP_STATE = [
  /^qyngent[.:_-]/i, // qyngent.foo, qyngent:foo, qyngent_foo, qyngent-foo
  /^active[_-]?(project|application|workspace|org)/i,
  /^(project|application|workspace|org)[_-]?id$/i,
]

/**
 * Keys that must SURVIVE a user change.
 *
 * The session token is the critical one: `clearAllSessionState` runs while the
 * NEW session is being established, so purging it would sign the user straight
 * back out. The rest are browser-level preferences with no account identity in
 * them.
 */
const PRESERVE = [
  /access[_-]?token/i,
  /refresh[_-]?token/i,
  /^supabase\./i,
  /sidebar/i,
  /visitor/i,
  /theme|colou?r-?scheme/i,
]

/** True when `key` is per-account app state that must not outlive a user change. */
export function shouldPurgeOnUserChange(key: string): boolean {
  if (PRESERVE.some((re) => re.test(key))) return false
  return APP_STATE.some((re) => re.test(key))
}

/**
 * The keys currently present in `storage` that must be purged.
 *
 * Takes the storage object so it can be tested directly, and so a caller can
 * log exactly what was removed rather than guessing.
 */
export function keysToPurgeOnUserChange(storage: Storage): string[] {
  const out: string[] = []
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i)
    if (key && shouldPurgeOnUserChange(key)) out.push(key)
  }
  return out
}
